import { connect } from 'react-redux';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerFooter,
  ProgressBar,
  Badge,
  Textarea,
} from '@fluentui/react-components';
import {
  CheckmarkCircle24Filled,
  ArrowSyncDismiss24Filled,
  SyncOff16Regular,
  WarningFilled,
  BranchRegular,
  HistoryRegular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { FontIcon, Separator } from '@fluentui/react';

import * as ComponentFunctions from './Contract.IAImport.Functions';
import { ContractIAImportProcess } from '../../../models/Entities/Contracts/ContractIAImportProcess';
import FileSelectorComponent from '../../../commons/component/Inputs/FileSelector/FileSelector.Component';
import ContractIAImportFeedbackComponent from './Contract.IAImport.Feedback.Component';
import './Contract.IAImport.Styles.css';

// Status: 1 = registered/pending, 2 = inProgress, 3 = finished, 4 = cancelled, 5 = fail
type StatusVariant = 'idle' | 'running' | 'success' | 'cancelled' | 'error';

function getStatusVariant(status?: number): StatusVariant {
  switch (status) {
    case 1:
    case 2:
      return 'running';
    case 3:
      return 'success';
    case 4:
      return 'cancelled';
    case 5:
      return 'error';
    default:
      return 'idle';
  }
}

const StatusIcon = ({ status, isLoading }: { status?: number; isLoading?: boolean }) => {
  if (isLoading) return <Spinner size="extra-tiny" />;
  switch (status) {
    case 1:
    case 2:
      return <Spinner size="extra-tiny" />;
    case 3:
      return <CheckmarkCircle24Filled className="ia-import-status__icon--success" />;
    case 4:
      return <SyncOff16Regular className="ia-import-status__icon--cancelled" />;
    case 5:
      return <ArrowSyncDismiss24Filled className="ia-import-status__icon--error" />;
    default:
      return <FontIcon iconName="CloudUpload" className="ia-import-status__icon--idle" />;
  }
};

const ContractIAImportComponent = (props: ComponentFunctions.Props) => {
  const { contractId, lastProcess } = props;
  const { t } = useTranslation();

  const [fileIds, setFileIds] = useState<string[]>([]);
  const [hasPassword, setHasPassword] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [reWriteExistingData, setReWriteExistingData] = useState<boolean>(false);
  const [comments, setComments] = useState<string>('');
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const lastProcessIdRef = useRef<string | undefined>(undefined);

  // When IA import finishes, refresh addendums/stages/concepts
  ComponentFunctions.useIAImportCompletionRefresh(
    contractId,
    lastProcess,
    props.GetAllContractStagesByAddendumId,
    props.GetAllContractsConceptByAddendum,
    props.GetContractAddendumsByContractId
  );

  // Refetch whenever the contract changes. The old guard also required
  // lastProcess to be null, so switching contracts never refetched: the previous
  // contract's process was non-null and left the widget frozen on stale data.
  useEffect(() => {
    if (contractId) {
      props.GetLastImportContractProcess(contractId);
    }
  }, [contractId]);

  // Poll every 5s while status is 1 or 2
  useEffect(() => {
    const isActiveStatus = lastProcess && (lastProcess.status === 1 || lastProcess.status === 2);
    if (!isActiveStatus) return;
    const interval = setInterval(() => {
      props.GetLastImportContractProcess(contractId);
    }, 5000);
    return () => clearInterval(interval);
  }, [lastProcess?.status, contractId]);

  // Fetch once after successful creation
  useEffect(() => {
    if (props.successCreating) {
      props.GetLastImportContractProcess(contractId);
    }
  }, [props.successCreating]);

  // Elapsed time counter while running
  useEffect(() => {
    const active = !!lastProcess && (lastProcess.status === 1 || lastProcess.status === 2);
    if (!active) {
      setElapsedTime(0);
      lastProcessIdRef.current = undefined;
      return;
    }
    if (lastProcess?.id && lastProcessIdRef.current !== lastProcess.id) {
      lastProcessIdRef.current = lastProcess.id;
    }

    const calculateElapsed = () => {
      if (lastProcess.startTimestamp) {
        let ts = lastProcess.startTimestamp as string;
        if (typeof ts === 'string') {
          ts = ts.replace(/(\.\d{3})\d+/, '$1');
          if (!ts.endsWith('Z')) ts += 'Z';
        }
        const elapsed = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
        return elapsed > 0 ? elapsed : 0;
      }
      return 0;
    };

    setElapsedTime(calculateElapsed());
    const timer = setInterval(() => setElapsedTime(calculateElapsed()), 1000);
    return () => clearInterval(timer);
  }, [lastProcess?.id, lastProcess?.status, lastProcess?.startTimestamp]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (durationString?: string): string => {
    if (!durationString) return '—';
    const parts = durationString.split(':');
    if (parts.length >= 2) {
      const h = parseInt(parts[0] || '0', 10);
      const m = parseInt(parts[1] || '0', 10);
      const s = parseInt(parts[2] || '0', 10);
      return formatTime(h * 3600 + m * 60 + s);
    }
    const total = parseInt(durationString, 10);
    return isNaN(total) ? '—' : formatTime(total);
  };

  const formatDateTime = (raw?: Date | string | null): string | null => {
    if (!raw) return null;
    return new Date(raw as string).toLocaleString(undefined, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const isRunning = !!lastProcess && (lastProcess.status === 1 || lastProcess.status === 2);
  const isDone = !!lastProcess && (lastProcess.status === 3 || lastProcess.status === 4 || lastProcess.status === 5);
  const statusVariant = getStatusVariant(lastProcess?.status);
  const isInitialLoading = props.isGettingLast && lastProcess == null;

  const canStart = useMemo(
    () => !!contractId && fileIds.length > 0 && !props.isCreating,
    [contractId, fileIds, props.isCreating]
  );

  const statusLabelMap: Record<StatusVariant, string> = {
    idle: 'Sin importaciones previas',
    running: lastProcess?.status === 1 ? 'Iniciando importación...' : 'Realizando importación...',
    success: 'Importación completada',
    cancelled: 'Importación cancelada',
    error: 'Error en la importación',
  };

  const statusNameMap: Record<StatusVariant, string> = {
    idle: 'Sin estado',
    running: 'En curso',
    success: 'Completada',
    cancelled: 'Cancelada',
    error: 'Con error',
  };

  const lastTimestamp = lastProcess && (lastProcess.endTimestamp || lastProcess.dateTimeSync || lastProcess.startTimestamp);
  const lastDateFormatted = formatDateTime(lastTimestamp as string | null);

  const onOpenForm = () => {
    setOpenDrawer(true);
    props.GetImportContractProcessHistory(contractId);
  };
  const onCloseForm = () => setOpenDrawer(false);
  const onStartImport = () => {
    props.CreateImportContractProcess({
      contractID: contractId,
      fileIds,
      hasPassword: !!hasPassword,
      reWriteExistingData: !!reWriteExistingData,
      password: hasPassword ? password : undefined,
      additionalPromptInformation: comments?.trim() || undefined,
    });
    setOpenDrawer(false);
  };

  const onLocalFilesChange = (files: any[]) => {
    setFileIds(files?.map(file => String(file.fileId || file.id)).filter(Boolean) || []);
  };

  return (
    <div className="ia-import-widget">

      {/* ── Header ── */}
      <div className="ia-import-widget__header">
        <div className="ia-import-widget__header-left">
          <FontIcon iconName="CloudImport" className="ia-import-widget__header-icon" />
          <span className="ia-import-widget__title">Importación IA</span>
          <Badge appearance="filled" color="warning" size="small">BETA</Badge>
        </div>
        <button
          type="button"
          className="ia-import-widget__refresh-btn"
          title={t('contractIAImport.actions.refresh')}
          disabled={props.isGettingLast}
          onClick={() => props.GetLastImportContractProcess(contractId)}
        >
          <FontIcon iconName="Refresh" />
        </button>
      </div>

      {/* ── Status ── */}
      <div className={`ia-import-status ia-import-status--${statusVariant}`}>
        <div className="ia-import-status__icon-col">
          <StatusIcon status={lastProcess?.status} isLoading={isInitialLoading} />
        </div>
        <div className="ia-import-status__content">
          {isInitialLoading ? (
            <span className="ia-import-status__label">Verificando estado...</span>
          ) : (
            <>
              <span className="ia-import-status__label">{statusLabelMap[statusVariant]}</span>

              {isRunning && (
                <div className="ia-import-metrics">
                  <div className="ia-import-metric">
                    <span className="ia-import-metric__label">Transcurrido</span>
                    <span className="ia-import-metric__value">
                      <FontIcon iconName="Clock" />
                      {formatTime(elapsedTime)}
                    </span>
                  </div>
                </div>
              )}

              {isDone && (
                <div className="ia-import-metrics">
                  {lastDateFormatted && (
                    <div className="ia-import-metric">
                      <span className="ia-import-metric__label">Última ejecución</span>
                      <span className="ia-import-metric__value">{lastDateFormatted}</span>
                    </div>
                  )}
                  {lastProcess?.duration && (
                    <div className="ia-import-metric">
                      <span className="ia-import-metric__label">Duración</span>
                      <span className="ia-import-metric__value">{formatDuration(lastProcess.duration)}</span>
                    </div>
                  )}
                </div>
              )}

              {statusVariant === 'idle' && (
                <span className="ia-import-status__hint">
                  Subí los archivos del contrato y la IA los interpreta para cargar etapas y conceptos.
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Progress bar while running ── */}
      {isRunning && (
        <div className="ia-import-widget__progress">
          <ProgressBar />
        </div>
      )}

      {/* ── Process error detail ── */}
      {lastProcess?.status === 5 && lastProcess.errorMessage && (
        <div className="ia-import-widget__process-error">
          <FontIcon iconName="ErrorBadge" />
          <span>{lastProcess.errorMessage}</span>
        </div>
      )}

      {/* ── Feedback de la importación ── */}
      {/* Va después del estado y antes del botón: la puntuación pertenece a la
          ejecución que se acaba de leer, no a la próxima que se va a lanzar. */}
      {isDone && <ContractIAImportFeedbackComponent process={lastProcess} />}

      {/* ── Action button ── */}
      {!isRunning && (
        <div className="ia-import-widget__actions">
          <Button
            appearance="primary"
            className="ia-import-widget__cta"
            icon={<FontIcon iconName="CloudUpload" />}
            onClick={onOpenForm}
            disabled={props.isCreating}
          >
            {props.isCreating
              ? 'Iniciando...'
              : (isDone ? 'Nueva importación' : t('contractIAImport.actions.openForm'))}
          </Button>
        </div>
      )}

      {/* ── API errors ── */}
      {(props.failureCreating || props.failureGettingLast) && (
        <MessageBar intent="error" className="ia-import-widget__api-error">
          <MessageBarBody>
            <MessageBarTitle>{t('contractIAImport.error.title')}</MessageBarTitle>
            {props.errorCreating?.ErrorMessage || props.errorGettingLast?.ErrorMessage}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* ── Beta footer ── */}
      <div className="ia-import-widget__beta-footer">
        <FontIcon iconName="TestBeaker" className="ia-import-widget__beta-footer-icon" />
        <span>Funcionalidad en evaluación: los resultados pueden variar.</span>
      </div>

      {/* ── Import Drawer ── */}
      {/* medium (592px) rather than the default small (320px): the form now
          carries the file list, the destination block and the history, and at
          320px the file names and history rows had no room to breathe. */}
      <Drawer
        position="end"
        size="medium"
        open={openDrawer}
        onOpenChange={(_, data) => setOpenDrawer(data.open)}
      >
        <DrawerHeader className="ia-import-drawer__header">
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Cerrar"
                icon={<FontIcon iconName="Cancel" />}
                onClick={onCloseForm}
              />
            }
          >
            {t('contractIAImport.form.title')}
          </DrawerHeaderTitle>
          <span className="ia-import-drawer__subtitle">
            La IA lee los archivos del contrato y propone etapas y conceptos.
            Revisá el destino antes de iniciar.
          </span>
        </DrawerHeader>

        <DrawerBody>
          <form autoComplete="off" className="ia-import-form">

            {/* ── Archivos ── */}
            <section className="ia-import-card">
              <header className="ia-import-card__header">
                <span className="ia-import-card__icon">
                  <FontIcon iconName="Attach" />
                </span>
                <div className="ia-import-card__heading">
                  <span className="ia-import-card__title">Archivos</span>
                  <span className="ia-import-card__desc">
                    Contrato, adendas y anexos que la IA va a interpretar.
                  </span>
                </div>
                {fileIds.length > 0 && (
                  <span className="ia-import-card__count">{fileIds.length}</span>
                )}
              </header>
              <div className="ia-import-card__body">
                <FileSelectorComponent contractId={contractId} multiple={true} onChange={onLocalFilesChange} />
                {fileIds.length === 0 && (
                  <p className="ia-import-card__hint">
                    {t('contractIAImport.form.fileId.hint')}
                  </p>
                )}
              </div>
            </section>

            {/* ── Destino ── */}
            {/* Placed before the options: it is the decision with consequences,
                and the safe outcome is stated before the switch that undoes it. */}
            <section className="ia-import-card">
              <header className="ia-import-card__header">
                <span className="ia-import-card__icon">
                  <FontIcon iconName="Financial" />
                </span>
                <div className="ia-import-card__heading">
                  <span className="ia-import-card__title">Destino de la importación</span>
                  <span className="ia-import-card__desc">
                    Dónde se van a escribir los datos detectados.
                  </span>
                </div>
              </header>
              <div className="ia-import-card__body">
                {!reWriteExistingData && (
                  <div className="ia-import-destination">
                    <BranchRegular className="ia-import-destination__icon" />
                    <div>
                      <span className="ia-import-destination__title">
                        Se importará en una adenda aparte
                      </span>
                      <span className="ia-import-destination__text">
                        Los datos actuales del contrato quedan intactos: la importación crea una
                        adenda nueva con lo que se detecte en los archivos.
                      </span>
                    </div>
                  </div>
                )}

                <div className={reWriteExistingData ? 'ia-import-danger ia-import-danger--armed' : 'ia-import-danger'}>
                  <div className="ia-import-danger__header">
                    <WarningFilled className="ia-import-danger__icon" />
                    <span className="ia-import-danger__title">Zona de riesgo</span>
                  </div>
                  <Checkbox
                    className="ia-import-danger__checkbox"
                    checked={reWriteExistingData}
                    onChange={(_, d) => setReWriteExistingData(!!d.checked)}
                    label={t('contractIAImport.form.reWriteExistingData')}
                  />
                  <p className="ia-import-danger__text">
                    Sobrescribir los datos del contrato puede generar complicaciones en contratos
                    con liquidaciones ya realizadas: los valores con los que se liquidó pueden dejar
                    de coincidir con los del contrato.
                  </p>
                </div>
              </div>
            </section>

            {/* ── Opciones ── */}
            <section className="ia-import-card">
              <header className="ia-import-card__header">
                <span className="ia-import-card__icon">
                  <FontIcon iconName="Settings" />
                </span>
                <div className="ia-import-card__heading">
                  <span className="ia-import-card__title">Opciones del archivo</span>
                  <span className="ia-import-card__desc">
                    Sólo hace falta si el archivo está protegido.
                  </span>
                </div>
              </header>
              <div className="ia-import-card__body">
                <Checkbox
                  checked={hasPassword}
                  onChange={(_, d) => setHasPassword(!!d.checked)}
                  label={t('contractIAImport.form.passwordProtected')}
                />
                {hasPassword && (
                  <Field label={t('contractIAImport.form.password')}>
                    <Input
                      type="password"
                      value={password}
                      onChange={(_, d) => setPassword(d.value)}
                      name="contract-file-password"
                      autoComplete="new-password"
                    />
                  </Field>
                )}
              </div>
            </section>

            {/* ── Contexto ── */}
            <section className="ia-import-card">
              <header className="ia-import-card__header">
                <span className="ia-import-card__icon">
                  <FontIcon iconName="Comment" />
                </span>
                <div className="ia-import-card__heading">
                  <span className="ia-import-card__title">Contexto adicional</span>
                  <span className="ia-import-card__desc">
                    {t('contractIAImport.form.additionalPromptInformation.hint')}
                  </span>
                </div>
              </header>
              <div className="ia-import-card__body">
                {/* A textarea, not a one-line input: this is free-form guidance
                    for the model and was being typed into a field that hid it. */}
                <Textarea
                  rows={3}
                  resize="vertical"
                  value={comments}
                  onChange={(_, d) => setComments(d.value)}
                  name="contract-file-comments"
                  autoComplete="off"
                  placeholder="Ej.: los honorarios del investigador están en el anexo B."
                />
              </div>
            </section>

            {/* ── Historial ── */}
            <section className="ia-import-card">
              <header className="ia-import-card__header">
                <span className="ia-import-card__icon">
                  <HistoryRegular />
                </span>
                <div className="ia-import-card__heading">
                  <span className="ia-import-card__title">Importaciones anteriores</span>
                  <span className="ia-import-card__desc">
                    Últimas ejecuciones sobre este contrato.
                  </span>
                </div>
                {props.history.length > 0 && (
                  <span className="ia-import-card__count">{props.history.length}</span>
                )}
              </header>
              <div className="ia-import-card__body">
                {props.isGettingHistory && (
                  <div className="ia-import-history__loading">
                    <Spinner size="extra-tiny" />
                    <span>Cargando historial...</span>
                  </div>
                )}

                {!props.isGettingHistory && props.history.length === 0 && (
                  <p className="ia-import-history__empty">
                    Todavía no se realizaron importaciones en este contrato.
                  </p>
                )}

                {!props.isGettingHistory && props.history.length > 0 && (
                  <ul className="ia-import-history">
                    {props.history.map((process: ContractIAImportProcess) => {
                      const variant = getStatusVariant(process.status);
                      return (
                        <li key={process.id} className={'ia-import-history__item ia-import-history__item--' + variant}>
                          <div className="ia-import-history__row">
                            <span className={'ia-import-history__status ia-import-history__status--' + variant}>
                              {statusNameMap[variant]}
                            </span>
                            <span className="ia-import-history__date">
                              {formatDateTime(process.startTimestamp as string) || '—'}
                            </span>
                          </div>
                          <div className="ia-import-history__meta">
                            <span>Duración: {formatDuration(process.duration)}</span>
                          </div>
                          {process.errorMessage && (
                            <p className="ia-import-history__error">{process.errorMessage}</p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>

          </form>
        </DrawerBody>

        <DrawerFooter>
          <div className="ia-import-form__footer">
            {/* Restates the destination at the moment of committing, where it
                matters most and where the section above may be scrolled away. */}
            <span className={reWriteExistingData
              ? 'ia-import-form__footer-note ia-import-form__footer-note--danger'
              : 'ia-import-form__footer-note'}>
              {reWriteExistingData
                ? 'Sobrescribe el contrato actual'
                : 'Se creará una adenda nueva'}
            </span>
            <div className="ia-import-form__footer-actions">
              <Button appearance="secondary" onClick={onCloseForm}>
                {t('contractIAImport.actions.cancel')}
              </Button>
              <Button appearance="primary" disabled={!canStart} onClick={onStartImport}>
                {props.isCreating ? 'Iniciando...' : t('contractIAImport.actions.start')}
              </Button>
            </div>
          </div>
        </DrawerFooter>
      </Drawer>

    </div>
  );
};

export default connect(ComponentFunctions.mapStateToProps, ComponentFunctions.mapDispatchToProps)(ContractIAImportComponent as any);
